import React, { useEffect, useState, useCallback, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import {
  Box,
  Typography,
  Paper,
  Button,
  CircularProgress,
  Chip,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Card,
  CardContent,
  Divider,
  Collapse,
  Tooltip,
} from '@mui/material';
import Dialog from '../components/AppDialog';
import {
  Person,
  Delete as DeleteIcon,
  Circle,
  Schedule,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  HourglassEmpty,
  CheckCircle,
  Cancel as CancelIcon,
  EventAvailable,
  EventBusy,
  History as HistoryIcon,
} from '@mui/icons-material';
import dayjs from 'dayjs';
import api from '../api/axios';
import PullToRefresh from 'react-simple-pull-to-refresh';
import { useDarkMode } from '../App';
import { useDialog } from '../contexts/DialogProvider';

// 요일 표시 함수
const getDayOfWeek = (dateStr) => {
  const days = ['일', '월', '화', '수', '목', '금', '토'];
  return days[dayjs(dateStr).day()];
};

// 섹션 헤더 (접기/펼치기) — 렌더마다 재생성되지 않도록 모듈 스코프로 분리
function SectionHeader({ title, count, expanded, onToggle, icon, colorType, darkMode }) {
  const getBgColor = () => {
    switch (colorType) {
      case 'warning': return darkMode ? 'rgba(255, 152, 0, 0.15)' : '#fff3e0';
      case 'error': return darkMode ? 'rgba(211, 47, 47, 0.15)' : '#ffebee';
      case 'info': return darkMode ? 'rgba(25, 118, 210, 0.15)' : '#e3f2fd';
      default: return darkMode ? 'rgba(255, 255, 255, 0.08)' : '#f5f5f5';
    }
  };

  return (
    <Box
      onClick={onToggle}
      sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        p: 1.5,
        mb: 1,
        borderRadius: 1.5,
        backgroundColor: getBgColor(),
        cursor: 'pointer',
        transition: 'all 0.2s',
        '&:hover': {
          opacity: 0.9,
        },
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        {icon}
        <Typography variant="subtitle2" sx={{ fontWeight: 700, fontSize: '0.9rem' }}>
          {title}
        </Typography>
        {count > 0 && (
          <Chip
            label={count}
            size="small"
            sx={{
              height: 20,
              fontSize: '0.75rem',
              fontWeight: 700,
              backgroundColor: darkMode ? 'rgba(255,255,255,0.2)' : 'rgba(255,255,255,0.9)',
              color: darkMode ? '#fff' : 'inherit',
            }}
          />
        )}
      </Box>
      <IconButton size="small" sx={{ color: 'inherit' }}>
        {expanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
      </IconButton>
    </Box>
  );
}

// 승인 대기 중 신청 카드 (레슨 신청 / 레슨 취소 신청 공용)
function PendingRequestCard({ request, variant, darkMode, onCancel }) {
  const cfg = variant === 'cancellation'
    ? {
        border: 'error.main',
        bg: darkMode ? 'rgba(244, 67, 54, 0.1)' : '#ffebee',
        badgeBg: '#f44336',
        badgeLabel: '레슨 취소 신청',
        badgeIcon: <EventBusy sx={{ fontSize: 14 }} />,
        accent: darkMode ? '#ef9a9a' : '#c62828',
        teacherBg: '#f44336',
        hoverBg: darkMode ? 'rgba(244,67,54,0.25)' : '#ffcdd2',
      }
    : {
        border: 'warning.main',
        bg: darkMode ? 'rgba(255, 152, 0, 0.1)' : '#fff8e1',
        badgeBg: '#4caf50',
        badgeLabel: '레슨 신청',
        badgeIcon: <EventAvailable sx={{ fontSize: 14 }} />,
        accent: darkMode ? '#ffb74d' : '#e65100',
        teacherBg: '#ff9800',
        hoverBg: darkMode ? 'rgba(255,152,0,0.25)' : '#ffebee',
      };

  return (
    <Card
      sx={{
        borderRadius: 1.5,
        boxShadow: '0 1px 3px rgba(0,0,0,0.08)',
        border: '2px solid',
        borderColor: cfg.border,
        backgroundColor: cfg.bg,
        position: 'relative',
      }}
    >
      <CardContent sx={{ p: 1.5, pr: 6, '&:last-child': { pb: 1.5 } }}>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.8 }}>
          {/* 신청 타입 배지 */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Chip
              icon={cfg.badgeIcon}
              label={cfg.badgeLabel}
              size="small"
              sx={{
                backgroundColor: cfg.badgeBg,
                color: '#fff',
                fontWeight: 700,
                height: 20,
                fontSize: '0.7rem',
              }}
            />
          </Box>

          {/* 날짜 + 요일 */}
          <Typography variant="body2" sx={{ fontWeight: 700, fontSize: '0.95rem', color: cfg.accent }}>
            {request.date} ({getDayOfWeek(request.date)})
          </Typography>

          {/* 시간 & 선생님 */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <Schedule sx={{ fontSize: 16, color: cfg.accent }} />
              <Typography variant="body2" sx={{ fontSize: '0.85rem', color: cfg.accent }}>
                {request.start_time.slice(0, 5)} ~ {request.end_time.slice(0, 5)}
              </Typography>
            </Box>
            <Chip
              label={request.teacher?.name || '선생님'}
              size="small"
              sx={{
                backgroundColor: cfg.teacherBg,
                color: '#fff',
                fontWeight: 600,
                height: 22,
                fontSize: '0.75rem',
              }}
            />
          </Box>

          {/* 신청 시간 */}
          <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: '0.75rem' }}>
            신청일: {dayjs(request.created_at).format('YYYY-MM-DD HH:mm')}
          </Typography>
        </Box>

        {/* 취소 버튼 */}
        <Tooltip title="신청 취소">
          <IconButton
            size="small"
            color="error"
            onClick={() => onCancel(request)}
            aria-label="신청 취소"
            sx={{
              position: 'absolute',
              top: 8,
              right: 8,
              width: 32,
              height: 32,
              '&:hover': { backgroundColor: cfg.hoverBg },
            }}
          >
            <DeleteIcon sx={{ fontSize: 18 }} />
          </IconButton>
        </Tooltip>
      </CardContent>
    </Card>
  );
}

// 반려된 신청 카드
function RejectedRequestCard({ request, darkMode }) {
  const chipBg = darkMode ? 'rgba(255,255,255,0.12)' : '#e0e0e0';

  return (
    <Card
      sx={{
        borderRadius: 1.5,
        boxShadow: '0 1px 3px rgba(0,0,0,0.08)',
        border: '1px solid',
        borderColor: 'divider',
        bgcolor: darkMode ? 'grey.900' : '#fafafa',
        opacity: 0.8,
      }}
    >
      <CardContent sx={{ p: 1.5, '&:last-child': { pb: 1.5 } }}>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.8 }}>
          {/* 신청 타입 배지 */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Chip
              icon={request.type === 'RESERVATION' ? <EventAvailable sx={{ fontSize: 14 }} /> : <EventBusy sx={{ fontSize: 14 }} />}
              label={request.type === 'RESERVATION' ? '레슨 예약 신청' : '레슨 취소 신청'}
              size="small"
              sx={{
                backgroundColor: chipBg,
                color: 'text.secondary',
                fontWeight: 700,
                height: 20,
                fontSize: '0.7rem',
              }}
            />
          </Box>

          {/* 날짜 + 요일 */}
          <Typography variant="body2" sx={{ fontWeight: 700, fontSize: '0.9rem', color: 'text.secondary' }}>
            {request.date} ({getDayOfWeek(request.date)})
          </Typography>

          {/* 시간 & 선생님 */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <Schedule sx={{ fontSize: 16, color: 'text.disabled' }} />
              <Typography variant="body2" sx={{ fontSize: '0.85rem', color: 'text.disabled' }}>
                {request.start_time.slice(0, 5)} ~ {request.end_time.slice(0, 5)}
              </Typography>
            </Box>
            <Chip
              label={request.teacher?.name || '선생님'}
              size="small"
              sx={{
                backgroundColor: chipBg,
                color: 'text.secondary',
                fontWeight: 600,
                height: 22,
                fontSize: '0.75rem',
              }}
            />
          </Box>

          {/* 반려 사유 */}
          {request.rejection_reason && (
            <Box
              sx={{
                mt: 0.5,
                p: 1,
                borderRadius: 1,
                bgcolor: 'background.paper',
                border: '1px solid',
                borderColor: 'divider',
              }}
            >
              <Typography
                variant="caption"
                sx={{
                  color: darkMode ? '#ef9a9a' : '#d32f2f',
                  fontSize: '0.75rem',
                  display: 'block',
                  fontWeight: 600,
                  mb: 0.3,
                }}
              >
                반려 사유
              </Typography>
              <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: '0.75rem' }}>
                {request.rejection_reason}
              </Typography>
            </Box>
          )}
        </Box>
      </CardContent>
    </Card>
  );
}

// 레슨 타임라인 카드 (과거 / 미래 공용)
function LessonTimelineCard({ reservation, isPast, darkMode, hasPendingCancellation, onCancel, highlight = false, innerRef }) {
  if (isPast) {
    return (
      <Box sx={{ position: 'relative' }}>
        <Circle
          sx={{
            position: 'absolute',
            left: -21,
            top: 12,
            fontSize: 20,
            color: darkMode ? 'grey.600' : '#bdbdbd',
            bgcolor: 'background.paper',
            borderRadius: '50%',
          }}
        />
        <Card
          sx={{
            ml: 1,
            borderRadius: 1.5,
            boxShadow: '0 1px 3px rgba(0,0,0,0.08)',
            transition: 'all 0.2s ease',
            border: '1px solid',
            borderColor: 'divider',
            bgcolor: darkMode ? 'grey.900' : '#fafafa',
            opacity: 0.7,
            '&:hover': {
              boxShadow: '0 1px 3px rgba(0,0,0,0.08)',
              borderColor: 'divider',
            },
          }}
        >
          <CardContent sx={{ p: 1.5, '&:last-child': { pb: 1.5 } }}>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.8 }}>
              <Typography variant="body2" sx={{ fontWeight: 700, fontSize: '0.95rem', color: 'text.disabled' }}>
                {reservation.date} ({getDayOfWeek(reservation.date)})
              </Typography>

              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  <Schedule sx={{ fontSize: 16, color: 'text.disabled' }} />
                  <Typography variant="body2" sx={{ fontSize: '0.85rem', color: 'text.disabled' }}>
                    {reservation.start_time.slice(0, 5)} ~ {reservation.end_time.slice(0, 5)}
                  </Typography>
                </Box>
                <Chip
                  label={reservation.teacher?.name || '선생님'}
                  size="small"
                  sx={{
                    backgroundColor: darkMode ? 'rgba(255,255,255,0.08)' : '#f5f5f5',
                    color: 'text.disabled',
                    fontWeight: 600,
                    height: 22,
                    fontSize: '0.75rem',
                  }}
                />
              </Box>
            </Box>
          </CardContent>
        </Card>
      </Box>
    );
  }

  const isProvisional = reservation.is_provisional;
  const isRescheduled = reservation.is_rescheduled;
  // 취소 버튼이 없는 카드(임시 예약/변경 레슨/취소 대기)는 우측 여백을 비워둘 필요가 없음
  const showCancelButton = !hasPendingCancellation && !isProvisional && !isRescheduled;
  const dateColor = isProvisional
    ? (darkMode ? '#ffb74d' : '#e65100')
    : (darkMode ? '#64b5f6' : '#1976d2');

  return (
    <Box ref={innerRef} sx={{ position: 'relative', ...(highlight ? { scrollMarginTop: '72px' } : {}) }}>
      <Circle
        sx={{
          position: 'absolute',
          left: -21,
          top: 12,
          fontSize: 20,
          color: isProvisional ? 'warning.main' : 'primary.main',
          bgcolor: 'background.paper',
          borderRadius: '50%',
        }}
      />
      <Card
        sx={{
          ml: 1,
          borderRadius: 1.5,
          boxShadow: highlight
            ? `0 0 0 3px ${darkMode ? 'rgba(144,202,249,0.6)' : 'rgba(25,118,210,0.5)'}`
            : '0 1px 3px rgba(0,0,0,0.08)',
          transition: 'all 0.3s ease',
          border: '1px solid',
          borderColor: highlight
            ? 'primary.main'
            : (isProvisional ? 'warning.main' : (darkMode ? 'grey.700' : '#f0f0f0')),
          bgcolor: isProvisional
            ? (darkMode ? 'rgba(255, 152, 0, 0.1)' : '#fff3e0')
            : 'background.paper',
          '&:hover': {
            boxShadow: '0 2px 8px rgba(0,0,0,0.12)',
            borderColor: isProvisional ? 'warning.main' : 'primary.main',
          },
        }}
      >
        <CardContent sx={{ p: 1.5, pr: showCancelButton ? 6 : 1.5, '&:last-child': { pb: 1.5 } }}>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.8 }}>
            {/* 임시 예약 배지 */}
            {isProvisional && (
              <Box sx={{ mb: 0.5 }}>
                <Chip
                  label="다음 달 임시 예약"
                  size="small"
                  sx={{ backgroundColor: '#ff9800', color: '#fff', fontWeight: 700, height: 20, fontSize: '0.7rem' }}
                />
              </Box>
            )}
            {/* 일정 변경 예약 배지 */}
            {isRescheduled && (
              <Box sx={{ mb: 0.5 }}>
                <Chip
                  label="일정 변경 레슨"
                  size="small"
                  sx={{ backgroundColor: '#7b1fa2', color: '#fff', fontWeight: 700, height: 20, fontSize: '0.7rem' }}
                />
              </Box>
            )}

            <Typography variant="body2" sx={{ fontWeight: 700, fontSize: '0.95rem', color: dateColor }}>
              {reservation.date} ({getDayOfWeek(reservation.date)})
            </Typography>

            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <Schedule sx={{ fontSize: 16, color: 'text.secondary' }} />
                <Typography variant="body2" sx={{ fontSize: '0.85rem', color: 'text.secondary' }}>
                  {reservation.start_time.slice(0, 5)} ~ {reservation.end_time.slice(0, 5)}
                </Typography>
              </Box>
              <Chip
                label={reservation.teacher?.name || '선생님'}
                size="small"
                sx={{
                  backgroundColor: isProvisional
                    ? (darkMode ? 'rgba(237,108,2,0.25)' : '#ffe0b2')
                    : (darkMode ? 'rgba(25,118,210,0.25)' : '#e3f2fd'),
                  color: isProvisional
                    ? (darkMode ? '#ffb74d' : '#e65100')
                    : (darkMode ? '#90caf9' : '#1976d2'),
                  fontWeight: 600,
                  height: 22,
                  fontSize: '0.75rem',
                }}
              />
            </Box>

            {hasPendingCancellation && (
              <Box sx={{ mt: 0.5 }}>
                <Chip
                  label="취소 신청 대기 중"
                  size="small"
                  sx={{
                    backgroundColor: darkMode ? 'rgba(237,108,2,0.25)' : '#fff3e0',
                    color: darkMode ? '#ffb74d' : '#ed6c02',
                    fontWeight: 600,
                    height: 20,
                    fontSize: '0.7rem',
                  }}
                />
              </Box>
            )}

            {/* 임시 예약 안내 */}
            {isProvisional && (
              <Typography variant="caption" sx={{ color: darkMode ? '#ffb74d' : '#e65100', fontSize: '0.7rem', mt: 0.5 }}>
                * 다음 달 결제 후 정식 예약으로 전환됩니다
              </Typography>
            )}
            {/* 일정 변경 예약 안내 */}
            {isRescheduled && (
              <Typography variant="caption" sx={{ color: darkMode ? '#ce93d8' : '#7b1fa2', fontSize: '0.7rem', mt: 0.5, fontWeight: 600 }}>
                ⚠️ 변경 티켓으로 예약된 레슨입니다. 취소가 필요하신 경우 관리자에게 문의해주세요.
              </Typography>
            )}
          </Box>

          {/* 임시 예약 및 변경권 예약은 취소 버튼 비활성화 */}
          {showCancelButton && (
            <Tooltip title="취소 신청">
              <IconButton
                size="small"
                color="error"
                onClick={() => onCancel(reservation)}
                aria-label="취소 신청"
                sx={{
                  position: 'absolute',
                  top: 8,
                  right: 8,
                  width: 32,
                  height: 32,
                  '&:hover': { backgroundColor: darkMode ? 'rgba(244,67,54,0.2)' : '#ffebee' },
                }}
              >
                <DeleteIcon sx={{ fontSize: 18 }} />
              </IconButton>
            </Tooltip>
          )}
        </CardContent>
      </Card>
    </Box>
  );
}

// 연습실 예약 타임라인 카드 (과거 / 미래 공용)
function PracticeTimelineCard({ reservation, isPast, darkMode, onCancel }) {
  if (isPast) {
    return (
      <Box sx={{ position: 'relative' }}>
        <Circle
          sx={{
            position: 'absolute',
            left: -21,
            top: 12,
            fontSize: 20,
            color: darkMode ? 'grey.600' : '#bdbdbd',
            bgcolor: 'background.paper',
            borderRadius: '50%',
          }}
        />
        <Card
          sx={{
            ml: 1,
            borderRadius: 1.5,
            boxShadow: '0 1px 3px rgba(0,0,0,0.08)',
            transition: 'all 0.2s ease',
            border: '1px solid',
            borderColor: 'divider',
            bgcolor: darkMode ? 'grey.900' : '#fafafa',
            opacity: 0.7,
            '&:hover': {
              boxShadow: '0 1px 3px rgba(0,0,0,0.08)',
              borderColor: 'divider',
            },
          }}
        >
          <CardContent sx={{ p: 1.5, '&:last-child': { pb: 1.5 } }}>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.8 }}>
              <Typography variant="body2" sx={{ fontWeight: 700, fontSize: '0.95rem', color: 'text.disabled' }}>
                {reservation.date} ({getDayOfWeek(reservation.date)})
              </Typography>

              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap' }}>
                <Chip
                  label={reservation.room_name}
                  size="small"
                  sx={{
                    backgroundColor: darkMode ? 'rgba(255,255,255,0.08)' : '#f5f5f5',
                    color: 'text.disabled',
                    fontWeight: 600,
                    height: 22,
                    fontSize: '0.75rem',
                  }}
                />
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  <Schedule sx={{ fontSize: 16, color: 'text.disabled' }} />
                  <Typography variant="body2" sx={{ fontSize: '0.85rem', color: 'text.disabled' }}>
                    {reservation.start_time.slice(0, 5)} ~ {reservation.end_time.slice(0, 5)}
                  </Typography>
                </Box>
              </Box>
            </Box>
          </CardContent>
        </Card>
      </Box>
    );
  }

  return (
    <Box sx={{ position: 'relative' }}>
      <Circle
        sx={{
          position: 'absolute',
          left: -21,
          top: 12,
          fontSize: 20,
          color: 'primary.main',
          bgcolor: 'background.paper',
          borderRadius: '50%',
        }}
      />
      <Card
        sx={{
          ml: 1,
          borderRadius: 1.5,
          boxShadow: '0 1px 3px rgba(0,0,0,0.08)',
          transition: 'all 0.2s ease',
          border: '1px solid',
          borderColor: darkMode ? 'grey.700' : '#f0f0f0',
          bgcolor: 'background.paper',
          '&:hover': {
            boxShadow: '0 2px 8px rgba(0,0,0,0.12)',
            borderColor: 'primary.main',
          },
        }}
      >
        <CardContent sx={{ p: 1.5, pr: 6, '&:last-child': { pb: 1.5 } }}>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.8 }}>
            <Typography variant="body2" sx={{ fontWeight: 700, fontSize: '0.95rem', color: darkMode ? '#64b5f6' : '#1976d2' }}>
              {reservation.date} ({getDayOfWeek(reservation.date)})
            </Typography>

            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap' }}>
              <Chip
                label={reservation.room_name}
                size="small"
                sx={{
                  backgroundColor: '#1976d2',
                  color: '#fff',
                  fontWeight: 600,
                  height: 22,
                  fontSize: '0.75rem',
                }}
              />
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <Schedule sx={{ fontSize: 16, color: 'text.secondary' }} />
                <Typography variant="body2" sx={{ fontSize: '0.85rem', color: 'text.secondary' }}>
                  {reservation.start_time.slice(0, 5)} ~ {reservation.end_time.slice(0, 5)}
                </Typography>
              </Box>
            </Box>
          </Box>

          <Tooltip title="예약 취소">
            <IconButton
              size="small"
              color="error"
              onClick={() => onCancel(reservation)}
              aria-label="예약 취소"
              sx={{
                position: 'absolute',
                top: 8,
                right: 8,
                width: 32,
                height: 32,
                '&:hover': { backgroundColor: darkMode ? 'rgba(244,67,54,0.2)' : '#ffebee' },
              }}
            >
              <DeleteIcon sx={{ fontSize: 18 }} />
            </IconButton>
          </Tooltip>
        </CardContent>
      </Card>
    </Box>
  );
}

function MyInfoPage({ user }) {
  const { darkMode } = useDarkMode();
  const { toast } = useDialog();
  const location = useLocation();
  const focusCardRef = useRef(null);
  const rejectedSectionRef = useRef(null);
  const [focusLesson, setFocusLesson] = useState(null); // 알림에서 진입 시 강조할 확정 레슨 { date, time }
  const [focusRejected, setFocusRejected] = useState(false); // 반려 알림에서 진입 시 반려내역 포커싱
  const [activeTab, setActiveTab] = useState('lesson');
  const [loading, setLoading] = useState(false);
  const [lessonReservations, setLessonReservations] = useState([]);
  const [lessonRequests, setLessonRequests] = useState([]);
  const [practiceReservations, setPracticeReservations] = useState([]);
  const [cancelDialog, setCancelDialog] = useState({ open: false, reservation: null });
  const [canceling, setCanceling] = useState(false);

  // 섹션 접기/펼치기 상태
  const [pendingExpanded, setPendingExpanded] = useState(true);
  const [rejectedExpanded, setRejectedExpanded] = useState(false);
  const [confirmedExpanded, setConfirmedExpanded] = useState(true);

  // 반려된 신청 확인 여부 (로컬 스토리지에서 관리)
  const [rejectedCheckedTime, setRejectedCheckedTime] = useState(() => {
    if (!user?.id) return null;
    const stored = localStorage.getItem(`rejectedChecked_${user.id}`);
    return stored ? new Date(stored) : null;
  });

  // ✅ 과거 이력 표시 상태
  const [showPastLessons, setShowPastLessons] = useState(false);
  const [showPastPractice, setShowPastPractice] = useState(false);

  // 스케줄제 티켓 유효기간
  const [ticketBatches, setTicketBatches] = useState([]);

  // 알림에서 진입 시: 레슨 탭으로 이동하고 대상 섹션을 펼쳐 포커싱 (마운트 1회)
  useEffect(() => {
    const st = location.state || {};
    const fl = st.focusLesson;

    if (fl?.date) {
      // 레슨 승인 → 확정된 레슨 강조
      setActiveTab('lesson');
      setConfirmedExpanded(true);
      setFocusLesson({ date: fl.date, time: fl.time ? String(fl.time).slice(0, 5) : null });
    } else if (st.focusRejected) {
      // 반려 → 반려된 신청 섹션 포커싱
      setActiveTab('lesson');
      setRejectedExpanded(true);
      setFocusRejected(true);
    } else {
      return;
    }

    window.history.replaceState({}, document.title);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // 대상 확정 레슨 카드가 렌더되면 스크롤 + 잠시 강조 후 해제
  useEffect(() => {
    if (!focusLesson || !focusCardRef.current) return;
    focusCardRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
    const t = setTimeout(() => setFocusLesson(null), 2600);
    return () => clearTimeout(t);
  }, [focusLesson, lessonReservations]);

  // 반려내역 섹션이 렌더되면 스크롤
  useEffect(() => {
    if (!focusRejected || !rejectedSectionRef.current) return;
    rejectedSectionRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
    const t = setTimeout(() => setFocusRejected(false), 2600);
    return () => clearTimeout(t);
  }, [focusRejected, lessonRequests]);

  // 데이터 조회 (로딩 표시는 탭 로드 useEffect가 일괄 관리)
  const fetchLessonReservations = async () => {
    try {
      const res = await api.get('/lessons/my');
      setLessonReservations(res.data);
    } catch (err) {
      console.error('레슨 정보 조회 실패:', err);
      setLessonReservations([]);
    }
  };

  const fetchLessonRequests = async () => {
    try {
      const res = await api.get('/lesson-requests/my');
      setLessonRequests(res.data);
    } catch (err) {
      console.error('레슨 신청 내역 조회 실패:', err);
      setLessonRequests([]);
    }
  };

  const fetchPracticeReservations = async () => {
    try {
      const res = await api.get('/practices/my-reservations');
      setPracticeReservations(res.data);
    } catch (err) {
      console.error('연습실 예약 정보 조회 실패:', err);
      setPracticeReservations([]);
    }
  };

  const fetchTicketBatches = async () => {
    try {
      const res = await api.get(`/members/${user.id}/ticket-batches`);
      setTicketBatches(res.data);
    } catch (err) {
      console.error('티켓 유효기간 조회 실패:', err);
      setTicketBatches([]);
    }
  };

  // 레슨 탭 진입 시: 관련 데이터를 모두 받은 뒤에만 로딩 종료 (부분 노출 깜빡임 방지)
  useEffect(() => {
    if (activeTab !== 'lesson') return;
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const tasks = [fetchLessonReservations(), fetchLessonRequests()];
        if (user?.member_type === 'SCHEDULE') tasks.push(fetchTicketBatches());
        await Promise.all(tasks);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab, user?.id, user?.member_type]);

  // 연습실 탭 진입 시
  useEffect(() => {
    if (activeTab !== 'practice') return;
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        await fetchPracticeReservations();
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab, user?.id]);

  // 반려된 섹션을 펼칠 때 확인 시간 저장
  useEffect(() => {
    if (rejectedExpanded && user?.id) {
      const now = new Date();
      setRejectedCheckedTime(now);
      localStorage.setItem(`rejectedChecked_${user.id}`, now.toISOString());
    }
  }, [rejectedExpanded, user?.id]);

  // Pull-to-Refresh 핸들러 (전체 로딩 스피너 없이 갱신)
  const handleRefresh = useCallback(async () => {
    if (activeTab === 'lesson') {
      const promises = [fetchLessonReservations(), fetchLessonRequests()];
      if (user?.member_type === 'SCHEDULE') promises.push(fetchTicketBatches());
      await Promise.all(promises);
    } else {
      await fetchPracticeReservations();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab, user?.member_type]);

  // ✅ 레슨 취소 신청 (기존 직접 취소 대신)
  const handleCancelLessonReservation = async () => {
    if (!cancelDialog.reservation) return;

    // ✅ 임시 예약 확인
    if (cancelDialog.reservation.is_provisional) {
      toast('임시 예약은 취소 신청을 할 수 없습니다. 다음 달 결제 후 취소하실 수 있습니다.', 'warning');
      setCancelDialog({ open: false, reservation: null });
      return;
    }

    try {
      setCanceling(true);
      await api.post('/lesson-requests/cancellation', {
        lesson_id: cancelDialog.reservation.id
      });
      toast('레슨 취소 신청이 완료되었습니다. 관리자 승인 후 취소됩니다.');
      setCancelDialog({ open: false, reservation: null });
      await fetchLessonReservations();
      await fetchLessonRequests();
    } catch (err) {
      console.error('취소 신청 실패:', err);
      toast(err?.response?.data?.message || '취소 신청 실패', 'error');
    } finally {
      setCanceling(false);
    }
  };

  const handleCancelPracticeReservation = async () => {
    if (!cancelDialog.reservation) return;

    try {
      setCanceling(true);
      await api.delete(`/practices/${cancelDialog.reservation.id}`);
      toast('연습실 예약이 취소되었습니다.');
      setCancelDialog({ open: false, reservation: null });
      await fetchPracticeReservations();
    } catch (err) {
      console.error('예약 취소 실패:', err);
      toast(err?.response?.data?.message || '예약 취소 실패', 'error');
    } finally {
      setCanceling(false);
    }
  };

  const handleCancelLessonRequest = async () => {
    if (!cancelDialog.reservation) return;

    try {
      setCanceling(true);
      const response = await api.delete(`/lesson-requests/${cancelDialog.reservation.id}`);
      toast(response.data.message || '신청이 취소되었습니다.');
      setCancelDialog({ open: false, reservation: null });
      await fetchLessonRequests();
      await fetchLessonReservations();
    } catch (err) {
      console.error('신청 취소 실패:', err);
      toast(err?.response?.data?.message || '신청 취소 실패', 'error');
    } finally {
      setCanceling(false);
    }
  };

  const renderLessonTab = () => {
    // ✅ 신청 타입별로 분류
    const pendingReservations = lessonRequests.filter(r => r.status === 'PENDING' && r.type === 'RESERVATION');
    const pendingCancellations = lessonRequests.filter(r => r.status === 'PENDING' && r.type === 'CANCELLATION');
    const rejectedRequests = lessonRequests.filter(r => r.status === 'REJECTED').slice(0, 3);

    const totalPending = pendingReservations.length + pendingCancellations.length;

    // 확인하지 않은 반려 신청만 카운트
    const unreadRejectedCount = rejectedRequests.filter(r => {
      if (!rejectedCheckedTime) return true;
      return new Date(r.updated_at || r.created_at) > rejectedCheckedTime;
    }).length;

    // ✅ 과거/미래 레슨 분리
    const today = dayjs().startOf('day');
    const futureReservations = lessonReservations.filter(r => !dayjs(r.date).isBefore(today, 'day'));
    const pastReservations = lessonReservations.filter(r => dayjs(r.date).isBefore(today, 'day'));

    // 스케줄제 티켓 유효기간 - 오늘 기준 유효한 배치만
    const todayStr = dayjs().format('YYYY-MM-DD');
    const activeBatches = ticketBatches.filter(b => b.remaining > 0 && b.expires_at >= todayStr);
    const expiredBatches = ticketBatches.filter(b => b.remaining > 0 && b.expires_at < todayStr);

    return (
      <Box>

        {/* 티켓 현황 카드 */}
        {(user?.member_type === 'SCHEDULE' || user?.member_type === 'FIXED' || user?.member_type === 'COUNT') && (() => {
          const rescheduleCount = user?.reschedule_ticket_count ?? 0;
          const totalTicket = user?.ticket ?? 0;
          const regularCount = totalTicket - rescheduleCount;
          return (
            <Card
              sx={{
                mb: 2,
                borderRadius: 1.5,
                border: '1.5px solid',
                borderColor: user?.member_type === 'SCHEDULE'
                  ? (activeBatches.length > 0 ? 'primary.main' : (expiredBatches.length > 0 ? 'error.main' : 'divider'))
                  : (totalTicket > 0 ? 'primary.main' : 'divider'),
                backgroundColor: darkMode ? 'rgba(33,150,243,0.08)' : '#e3f2fd',
              }}
            >
              <CardContent sx={{ p: 1.5, '&:last-child': { pb: 1.5 } }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1, fontSize: '0.85rem', color: 'text.secondary' }}>
                  내 티켓 현황
                </Typography>

                {/* 일반 티켓 / 변경 티켓 요약 */}
                <Box sx={{ display: 'flex', gap: 1 }}>
                  <Box sx={{
                    flex: 1,
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    p: 1,
                    borderRadius: 1,
                    backgroundColor: darkMode ? 'rgba(25,118,210,0.25)' : '#bbdefb',
                    border: '1px solid',
                    borderColor: darkMode ? 'rgba(25,118,210,0.5)' : '#90caf9',
                  }}>
                    <Typography variant="caption" sx={{ color: darkMode ? '#90caf9' : '#1565c0', fontWeight: 600, mb: 0.3 }}>일반 티켓</Typography>
                    <Typography variant="h6" sx={{ fontWeight: 800, color: darkMode ? '#90caf9' : '#1565c0', lineHeight: 1 }}>{regularCount}<Typography component="span" variant="caption" sx={{ fontWeight: 600, ml: 0.3 }}>장</Typography></Typography>
                  </Box>
                  <Box sx={{
                    flex: 1,
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    p: 1,
                    borderRadius: 1,
                    backgroundColor: darkMode ? 'rgba(237,108,2,0.25)' : '#ffe0b2',
                    border: '1px solid',
                    borderColor: darkMode ? 'rgba(237,108,2,0.5)' : '#ffcc80',
                  }}>
                    <Typography variant="caption" sx={{ color: darkMode ? '#ffb74d' : '#e65100', fontWeight: 600, mb: 0.3 }}>변경 티켓</Typography>
                    <Typography variant="h6" sx={{ fontWeight: 800, color: darkMode ? '#ffb74d' : '#e65100', lineHeight: 1 }}>{rescheduleCount}<Typography component="span" variant="caption" sx={{ fontWeight: 600, ml: 0.3 }}>장</Typography></Typography>
                  </Box>
                </Box>

                {/* 스케줄제: 유효기간별 배치 상세 */}
                {user?.member_type === 'SCHEDULE' && (activeBatches.length > 0 || expiredBatches.length > 0) && (
                  <Box sx={{
                    mt: 1,
                    p: 1,
                    borderRadius: 1,
                    backgroundColor: darkMode ? 'rgba(0,0,0,0.2)' : 'rgba(255,255,255,0.7)',
                    border: '1px solid',
                    borderColor: darkMode ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.08)',
                  }}>
                    <Typography variant="caption" sx={{ fontWeight: 600, color: 'text.secondary', mb: 0.5, display: 'block' }}>
                      유효기간별 현황
                    </Typography>

                    {activeBatches.map((batch) => {
                      const isCancel = batch.source === 'CANCEL_REFUND';
                      const chipColor = isCancel ? 'warning' : 'primary';
                      return (
                        <Box
                          key={batch.id}
                          sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 0.5 }}
                        >
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Chip label={`${batch.remaining}장`} size="small" color={chipColor} sx={{ fontWeight: 700, minWidth: 48 }} />
                            <Typography variant="caption" color="text.secondary">
                              {isCancel ? '레슨 취소로 인한 티켓 반환' : (batch.description || '일반 티켓')}
                            </Typography>
                          </Box>
                          <Chip
                            label={`${batch.expires_at}까지`}
                            size="small"
                            variant="outlined"
                            color={chipColor}
                            sx={{ fontSize: '0.72rem' }}
                          />
                        </Box>
                      );
                    })}

                    {expiredBatches.length > 0 && (
                      <Box sx={{ mt: activeBatches.length > 0 ? 0.5 : 0 }}>
                        <Typography variant="caption" color="error" sx={{ fontWeight: 600 }}>
                          만료된 티켓 (관리자에게 문의하세요)
                        </Typography>
                        {expiredBatches.map((batch) => (
                          <Box key={batch.id} sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mt: 0.5 }}>
                            <Chip label={`${batch.remaining}장`} size="small" color="error" variant="outlined" sx={{ fontWeight: 700, minWidth: 48 }} />
                            <Chip label={`${batch.expires_at} 만료`} size="small" color="error" variant="outlined" sx={{ fontSize: '0.72rem' }} />
                          </Box>
                        ))}
                      </Box>
                    )}
                  </Box>
                )}
              </CardContent>
            </Card>
          );
        })()}

        {/* 승인 대기 중 섹션 */}
        {totalPending > 0 && (
          <Box sx={{ mb: 2 }}>
            <SectionHeader
              title="승인 대기 중"
              count={totalPending}
              expanded={pendingExpanded}
              onToggle={() => setPendingExpanded(!pendingExpanded)}
              icon={<HourglassEmpty sx={{ fontSize: 20, color: 'warning.main' }} />}
              colorType="warning"
              darkMode={darkMode}
            />
            <Collapse in={pendingExpanded}>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5, mt: 1 }}>
                {/* ✅ 레슨 신청 */}
                {pendingReservations.map((request) => (
                  <PendingRequestCard
                    key={request.id}
                    request={request}
                    variant="reservation"
                    darkMode={darkMode}
                    onCancel={(r) => setCancelDialog({ open: true, reservation: r, type: 'request' })}
                  />
                ))}

                {/* ✅ 레슨 취소 신청 */}
                {pendingCancellations.map((request) => (
                  <PendingRequestCard
                    key={request.id}
                    request={request}
                    variant="cancellation"
                    darkMode={darkMode}
                    onCancel={(r) => setCancelDialog({ open: true, reservation: r, type: 'request' })}
                  />
                ))}
              </Box>
            </Collapse>
          </Box>
        )}

        {/* 반려된 신청 섹션 */}
        {rejectedRequests.length > 0 && (
          <Box
            ref={focusRejected ? rejectedSectionRef : undefined}
            sx={{ mb: 2, ...(focusRejected ? { scrollMarginTop: '72px' } : {}) }}
          >
            <SectionHeader
              title="반려된 신청"
              count={unreadRejectedCount}
              expanded={rejectedExpanded}
              onToggle={() => setRejectedExpanded(!rejectedExpanded)}
              icon={<CancelIcon sx={{ fontSize: 20, color: 'error.main' }} />}
              colorType="error"
              darkMode={darkMode}
            />
            <Collapse in={rejectedExpanded}>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5, mt: 1 }}>
                {rejectedRequests.map((request) => (
                  <RejectedRequestCard key={request.id} request={request} darkMode={darkMode} />
                ))}
              </Box>
            </Collapse>
          </Box>
        )}

        {/* 확정된 레슨 섹션 */}
        <Box>
          <SectionHeader
            title="확정된 레슨"
            count={futureReservations.length}
            expanded={confirmedExpanded}
            onToggle={() => setConfirmedExpanded(!confirmedExpanded)}
            icon={<CheckCircle sx={{ fontSize: 20, color: 'primary.main' }} />}
            colorType="info"
            darkMode={darkMode}
          />
          <Collapse in={confirmedExpanded}>
            {futureReservations.length === 0 && pastReservations.length === 0 ? (
              <Box sx={{ textAlign: 'center', py: 4, mt: 1 }}>
                <Typography color="text.secondary" variant="body2">
                  {user?.member_type === 'FIXED' ? '고정 레슨 정보가 없습니다.' : '예약된 레슨이 없습니다.'}
                </Typography>
              </Box>
            ) : (
              <>
                {/* ✅ 이전 이력 보기 버튼 */}
                {pastReservations.length > 0 && (
                  <Box sx={{ mt: 1, mb: 2, display: 'flex', justifyContent: 'center' }}>
                    <Button
                      variant="outlined"
                      size="small"
                      startIcon={<HistoryIcon />}
                      onClick={() => setShowPastLessons(!showPastLessons)}
                      sx={{
                        borderRadius: 1.5,
                        textTransform: 'none',
                        fontSize: '0.85rem',
                        fontWeight: 600,
                        px: 2,
                        py: 0.8,
                      }}
                    >
                      {showPastLessons ? '과거 레슨 기록 숨기기' : `과거 레슨 기록 보기 (${pastReservations.length})`}
                    </Button>
                  </Box>
                )}

                <Box sx={{ position: 'relative', pl: 3, mt: showPastLessons ? 0 : 1 }}>
                  {/* 타임라인 선 */}
                  <Box
                    sx={{
                      position: 'absolute',
                      left: 8,
                      top: 12,
                      bottom: 12,
                      width: 2,
                      bgcolor: darkMode ? 'grey.700' : '#e0e0e0',
                    }}
                  />

                  {/* 타임라인 아이템들 */}
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                    {/* ✅ 과거 레슨 먼저 표시 */}
                    {showPastLessons && pastReservations.map((reservation, idx) => (
                      <LessonTimelineCard
                        key={`past-${idx}`}
                        reservation={reservation}
                        isPast
                        darkMode={darkMode}
                      />
                    ))}

                    {/* ✅ 미래 레슨 표시 */}
                    {futureReservations.map((reservation, idx) => {
                      const hasPendingCancellation = lessonRequests.some(
                        req => req.type === 'CANCELLATION' &&
                              req.lesson_id === reservation.id &&
                              req.status === 'PENDING'
                      );

                      const isFocused = !!focusLesson
                        && reservation.date === focusLesson.date
                        && (!focusLesson.time || reservation.start_time.slice(0, 5) === focusLesson.time);

                      return (
                        <LessonTimelineCard
                          key={`future-${idx}`}
                          reservation={reservation}
                          isPast={false}
                          darkMode={darkMode}
                          hasPendingCancellation={hasPendingCancellation}
                          onCancel={(r) => setCancelDialog({ open: true, reservation: r, type: 'lesson' })}
                          highlight={isFocused}
                          innerRef={isFocused ? focusCardRef : undefined}
                        />
                      );
                    })}
                  </Box>
                </Box>
              </>
            )}
          </Collapse>
        </Box>
      </Box>
    );
  };

  const renderPracticeTab = () => {
    if (!user?.practice_room_access) {
      return (
        <Box sx={{ textAlign: 'center', py: 6 }}>
          <Typography color="text.secondary" variant="body2">
            연습실 이용 권한이 없습니다.
          </Typography>
        </Box>
      );
    }

    // ✅ 과거/미래 예약 분리
    const today = dayjs().startOf('day');
    const futureReservations = practiceReservations.filter(r => !dayjs(r.date).isBefore(today, 'day'));
    const pastReservations = practiceReservations.filter(r => dayjs(r.date).isBefore(today, 'day'));

    if (futureReservations.length === 0 && pastReservations.length === 0) {
      return (
        <Box sx={{ textAlign: 'center', py: 6 }}>
          <Typography color="text.secondary" variant="body2">
            예약된 연습실이 없습니다.
          </Typography>
        </Box>
      );
    }

    return (
      <>
        {/* ✅ 이전 이력 보기 버튼 */}
        {pastReservations.length > 0 && (
          <Box sx={{ mb: 2, display: 'flex', justifyContent: 'center' }}>
            <Button
              variant="outlined"
              size="small"
              startIcon={<HistoryIcon />}
              onClick={() => setShowPastPractice(!showPastPractice)}
              sx={{
                borderRadius: 1.5,
                textTransform: 'none',
                fontSize: '0.85rem',
                fontWeight: 600,
                px: 2,
                py: 0.8,
              }}
            >
              {showPastPractice ? '과거 연습실 예약 기록 숨기기' : `과거 연습실 예약 기록 보기 (${pastReservations.length})`}
            </Button>
          </Box>
        )}

        <Box sx={{ position: 'relative', pl: 3 }}>
          {/* 타임라인 선 */}
          <Box
            sx={{
              position: 'absolute',
              left: 8,
              top: 12,
              bottom: 12,
              width: 2,
              bgcolor: darkMode ? 'grey.700' : '#e0e0e0',
            }}
          />

          {/* 타임라인 아이템들 */}
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            {/* ✅ 과거 예약 먼저 표시 */}
            {showPastPractice && pastReservations.map((reservation) => (
              <PracticeTimelineCard
                key={`past-${reservation.id}`}
                reservation={reservation}
                isPast
                darkMode={darkMode}
              />
            ))}

            {/* ✅ 미래 예약 표시 */}
            {futureReservations.map((reservation) => (
              <PracticeTimelineCard
                key={`future-${reservation.id}`}
                reservation={reservation}
                isPast={false}
                darkMode={darkMode}
                onCancel={(r) => setCancelDialog({ open: true, reservation: r, type: 'practice' })}
              />
            ))}
          </Box>
        </Box>
      </>
    );
  };

  return (
    <PullToRefresh
      onRefresh={handleRefresh}
      pullingContent={
        <Box sx={{ textAlign: "center", py: 2, color: "text.secondary" }}>
          ↓ 당겨서 새로고침
        </Box>
      }
      refreshingContent={
        <Box sx={{ textAlign: "center", py: 2, color: "primary.main" }}>
          새로고침 중...
        </Box>
      }
    >
    <Box sx={{ pt: 2, pb: 2, px: 2, maxWidth: 600, mx: 'auto' }}>
      {/* 사용자 정보 카드 */}
      <Paper
        elevation={3}
        sx={{
          p: 3,
          mb: 3.5,
          borderRadius: 2.5,
          background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
          color: '#fff',
          textAlign: 'center',
        }}
      >
        <Box sx={{ display: 'flex', justifyContent: 'center', mb: 2 }}>
          <Box
            sx={{
              width: 56,
              height: 56,
              borderRadius: '50%',
              backgroundColor: 'rgba(255,255,255,0.25)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              border: '2px solid rgba(255,255,255,0.5)',
            }}
          >
            <Person sx={{ fontSize: 28 }} />
          </Box>
        </Box>
        <Typography variant="h6" sx={{ fontWeight: 700, mb: 0.5 }}>
          {user?.name}
        </Typography>
        <Typography variant="caption" sx={{ opacity: 0.9, fontSize: '0.85rem' }}>
          {user?.member_type === 'FIXED' ? '고정제 회원' : user?.member_type === 'COUNT' ? '횟수제 회원' : '스케줄제 회원'}
        </Typography>
      </Paper>

      {/* 탭 버튼 */}
      <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 1.5, mb: 3.5 }}>
        <Button
          onClick={() => setActiveTab('lesson')}
          variant={activeTab === 'lesson' ? 'contained' : 'outlined'}
          sx={{
            py: 1.2,
            borderRadius: 1.5,
            textTransform: 'none',
            fontSize: '0.9rem',
            fontWeight: 700,
            transition: 'all 0.3s ease',
            backgroundColor: activeTab === 'lesson' ? 'primary.main' : 'transparent',
            color: activeTab === 'lesson' ? 'primary.contrastText' : 'primary.main',
            border: activeTab === 'lesson' ? 'none' : '2px solid',
            borderColor: activeTab === 'lesson' ? 'transparent' : 'primary.main',
            '&:hover': {
              backgroundColor: activeTab === 'lesson' ? 'primary.dark' : (darkMode ? 'rgba(144, 202, 249, 0.08)' : '#f0f7ff'),
            },
          }}
        >
          내 레슨
        </Button>
        <Button
          onClick={() => setActiveTab('practice')}
          variant={activeTab === 'practice' ? 'contained' : 'outlined'}
          sx={{
            py: 1.2,
            borderRadius: 1.5,
            textTransform: 'none',
            fontSize: '0.9rem',
            fontWeight: 700,
            transition: 'all 0.3s ease',
            backgroundColor: activeTab === 'practice' ? 'primary.main' : 'transparent',
            color: activeTab === 'practice' ? 'primary.contrastText' : 'primary.main',
            border: activeTab === 'practice' ? 'none' : '2px solid',
            borderColor: activeTab === 'practice' ? 'transparent' : 'primary.main',
            '&:hover': {
              backgroundColor: activeTab === 'practice' ? 'primary.dark' : (darkMode ? 'rgba(144, 202, 249, 0.08)' : '#f0f7ff'),
            },
          }}
        >
          연습실 예약
        </Button>
      </Box>

      {/* 콘텐츠 */}
      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
          <CircularProgress />
        </Box>
      ) : activeTab === 'lesson' ? (
        renderLessonTab()
      ) : (
        renderPracticeTab()
      )}

      {/* 확인 다이얼로그 */}
      <Dialog
        open={cancelDialog.open}
        onClose={() => setCancelDialog({ open: false, reservation: null })}
        maxWidth="xs"
        fullWidth
        PaperProps={{
          sx: { borderRadius: 2.5 }
        }}
      >
        <DialogTitle sx={{ fontWeight: 700, fontSize: '1.1rem', pb: 1 }}>
          {cancelDialog.type === 'lesson' ? '레슨 취소 신청' : cancelDialog.type === 'request' ? (cancelDialog.reservation?.type === 'RESERVATION' ? '레슨 신청 취소' : '취소 신청 철회') : '연습실 예약 취소'}
        </DialogTitle>
        <Divider />
        <DialogContent sx={{ pt: 2.5 }}>
          <Typography sx={{ mb: 2.5, fontSize: '0.95rem', color: 'text.primary' }}>
            {cancelDialog.type === 'lesson'
              ? '레슨 취소 신청을 하시겠습니까? 관리자 승인 후 취소됩니다.'
              : cancelDialog.type === 'request'
                ? (cancelDialog.reservation?.type === 'RESERVATION' ? '레슨 신청을 취소하시겠습니까?' : '레슨 취소 신청을 철회하시겠습니까?')
                : '연습실 예약을 취소하시겠습니까?'
            }
          </Typography>
          {cancelDialog.reservation && (
            <Card variant="outlined" sx={{ backgroundColor: darkMode ? 'grey.800' : '#fafafa', borderRadius: 1.5 }}>
              <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                  <Box>
                    <Typography
                      variant="caption"
                      sx={{
                        display: 'block',
                        color: 'text.secondary',
                        fontWeight: 700,
                        fontSize: '0.75rem',
                        letterSpacing: '0.5px',
                        mb: 0.5,
                      }}
                    >
                      날짜
                    </Typography>
                    <Typography variant="body2" sx={{ fontWeight: 600, color: 'text.primary' }}>
                      {cancelDialog.reservation.date}
                    </Typography>
                  </Box>

                  <Divider sx={{ my: 0.5 }} />

                  {cancelDialog.type === 'lesson' || cancelDialog.type === 'request' ? (
                    <>
                      <Box>
                        <Typography
                          variant="caption"
                          sx={{
                            display: 'block',
                            color: 'text.secondary',
                            fontWeight: 700,
                            fontSize: '0.75rem',
                            letterSpacing: '0.5px',
                            mb: 0.5,
                          }}
                        >
                          시간
                        </Typography>
                        <Typography variant="body2" sx={{ fontWeight: 600, color: 'text.primary' }}>
                          {cancelDialog.reservation.start_time.slice(0, 5)} ~ {cancelDialog.reservation.end_time.slice(0, 5)}
                        </Typography>
                      </Box>

                      <Divider sx={{ my: 0.5 }} />

                      <Box>
                        <Typography
                          variant="caption"
                          sx={{
                            display: 'block',
                            color: 'text.secondary',
                            fontWeight: 700,
                            fontSize: '0.75rem',
                            letterSpacing: '0.5px',
                            mb: 0.5,
                          }}
                        >
                          선생님
                        </Typography>
                        <Typography variant="body2" sx={{ fontWeight: 600, color: 'text.primary' }}>
                          {cancelDialog.reservation.teacher?.name || '선생님'}
                        </Typography>
                      </Box>
                    </>
                  ) : (
                    <>
                      <Box>
                        <Typography
                          variant="caption"
                          sx={{
                            display: 'block',
                            color: 'text.secondary',
                            fontWeight: 700,
                            fontSize: '0.75rem',
                            letterSpacing: '0.5px',
                            mb: 0.5,
                          }}
                        >
                          연습실
                        </Typography>
                        <Typography variant="body2" sx={{ fontWeight: 600, color: 'text.primary' }}>
                          {cancelDialog.reservation.room_name}
                        </Typography>
                      </Box>

                      <Divider sx={{ my: 0.5 }} />

                      <Box>
                        <Typography
                          variant="caption"
                          sx={{
                            display: 'block',
                            color: 'text.secondary',
                            fontWeight: 700,
                            fontSize: '0.75rem',
                            letterSpacing: '0.5px',
                            mb: 0.5,
                          }}
                        >
                          시간
                        </Typography>
                        <Typography variant="body2" sx={{ fontWeight: 600, color: 'text.primary' }}>
                          {cancelDialog.reservation.start_time.slice(0, 5)} ~ {cancelDialog.reservation.end_time.slice(0, 5)}
                        </Typography>
                      </Box>
                    </>
                  )}
                </Box>
              </CardContent>
            </Card>
          )}
          {cancelDialog.type === 'lesson' && (
            <Typography variant="body2" sx={{ mt: 2, color: 'text.secondary', fontSize: '0.85rem' }}>
              * 관리자 승인 후 티켓이 복구됩니다.
            </Typography>
          )}
          {cancelDialog.type === 'request' && cancelDialog.reservation?.type === 'RESERVATION' && (
            <Typography variant="body2" sx={{ mt: 2, color: 'text.secondary', fontSize: '0.85rem' }}>
              * 신청을 취소하면 티켓이 복구됩니다.
            </Typography>
          )}
        </DialogContent>
        <Divider />
        <DialogActions sx={{ p: 2, gap: 1 }}>
          <Button
            onClick={() => setCancelDialog({ open: false, reservation: null })}
            disabled={canceling}
            variant="outlined"
            fullWidth
            sx={{ borderRadius: 1.5, fontWeight: 600 }}
          >
            취소
          </Button>
          <Button
            onClick={() => {
              if (cancelDialog.type === 'lesson') {
                handleCancelLessonReservation();
              } else if (cancelDialog.type === 'request') {
                handleCancelLessonRequest();
              } else {
                handleCancelPracticeReservation();
              }
            }}
            color="error"
            variant="contained"
            disabled={canceling}
            fullWidth
            sx={{ borderRadius: 1.5, fontWeight: 600 }}
          >
            {canceling ? '진행 중...' : '확인'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
    </PullToRefresh>
  );
}

export default MyInfoPage;
